import React, { useState } from 'react';
import { ExternalLink } from 'react-feather';
import { useTranslations } from 'use-intl';
import { PlanBData, SUPABASE_BUCKET } from '@/pages/CustomerSupport/types';
import { formatCurrency, formatDateTime } from '@/utils/utils';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';

interface PlanBDataSectionProps {
	planBData: PlanBData;
}

const PlanBDataSection: React.FC<PlanBDataSectionProps> = ({ planBData }) => {
	const t = useTranslations('SupportClients');
	const supabase = useSupabaseStore((state) => state.supabase);

	const [entrySignedUrls, setEntrySignedUrls] = useState<Record<number, string>>({});
	const [loadingEntries, setLoadingEntries] = useState<Record<number, boolean>>({});

	const handleImageClick = async (imageUrl: string, index: number) => {
		if (entrySignedUrls[index]) {
			window.open(entrySignedUrls[index], '_blank');
			return;
		}

		setLoadingEntries((prev) => ({ ...prev, [index]: true }));

		try {
			const pathMatch = imageUrl.match(`/${SUPABASE_BUCKET.WHATSAPP_IMAGES}/(.*)`);
			if (!pathMatch) {
				throw new Error('Invalid storage URL format');
			}

			const path = pathMatch[1];
			const { data, error } = await supabase.storage
				.from(SUPABASE_BUCKET.WHATSAPP_IMAGES)
				.createSignedUrl(path, 3600);

			if (error) throw error;

			setEntrySignedUrls((prev) => ({ ...prev, [index]: data.signedUrl }));
			window.open(data.signedUrl, '_blank');
		} catch (err) {
			console.error('Error getting signed URL:', err);
		} finally {
			setLoadingEntries((prev) => ({ ...prev, [index]: false }));
		}
	};

	const getDocumentTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			bank_statement: t('BankStatement'),
			payslip: t('Payslip'),
			invoice: t('Invoice'),
			other: t('Other')
		};
		return labels[type] || type;
	};

	const getIncomeValidityLabel = (validity: string) => {
		const labels: Record<string, string> = {
			low: t('Low'),
			medium: t('Medium'),
			high: t('High')
		};
		return labels[validity] || validity;
	};

	const getIncomeConsistencyLabel = (consistency: string) => {
		const labels: Record<string, string> = {
			low: t('Low'),
			medium: t('Medium'),
			high: t('High')
		};
		return labels[consistency] || consistency;
	};

	return (
		<div className="border-t pt-4">
			<h3 className="text-md font-semibold mb-3">{t('PlanBData')}</h3>

			{planBData.income_consistency && (
				<div className="mb-4">
					<label className="block text-sm text-gray-500 mb-1">{t('IncomeConsistency')}</label>
					<p className="text-sm font-medium">
						{getIncomeConsistencyLabel(planBData.income_consistency)}
					</p>
				</div>
			)}

			{planBData.entries && planBData.entries.length > 0 ? (
				<div className="space-y-4">
					{planBData.entries.map((entry, index) => (
						<div key={index} className="border rounded-lg p-4 bg-gray-50">
							<h4 className="text-sm font-semibold mb-3">
								{t('Entry')} {index + 1}
							</h4>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{entry.monthly_income !== undefined && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">{t('MonthlyIncome')}</label>
										<p className="text-sm font-medium">{formatCurrency(entry.monthly_income)}</p>
									</div>
								)}
								{entry.document_type && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">{t('DocumentType')}</label>
										<p className="text-sm font-medium">
											{getDocumentTypeLabel(entry.document_type)}
										</p>
									</div>
								)}
								{entry.document_date && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">{t('DocumentDate')}</label>
										<p className="text-sm font-medium">{formatDateTime(entry.document_date)}</p>
									</div>
								)}
								{entry.income_validity && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">
											{t('IncomeValidity')}
										</label>
										<p className="text-sm font-medium">
											{getIncomeValidityLabel(entry.income_validity)}
										</p>
									</div>
								)}
								{entry.image_url && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">{t('DocumentImage')}</label>
										<button
											type="button"
											onClick={() => handleImageClick(entry.image_url!, index)}
											disabled={loadingEntries[index]}
											className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1 disabled:text-gray-400"
										>
											{loadingEntries[index] ? t('LoadingImage') : t('ViewImage')}{' '}
											<ExternalLink size={14} />
										</button>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			) : (
				<p className="text-sm text-gray-500">{t('NoEntries')}</p>
			)}
		</div>
	);
};

export default PlanBDataSection;
